import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import { inject, observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import * as store from "../../stores/AuthStore";
import {DATE_UTIL} from "../../common/util/date.util";
import { useToast } from "../../components/useToast";
import { queuePokoroProfileCompleteReturnToSetting } from "../../common/pokoroProfileReturnFlow";
import SettingSubpageHeader from "../Setting/components/SettingSubpageHeader";
import CommonButton from "../CommonComponent/CommonButton";
import {
  PageRoot,
  Header,
  Title,
  Main,
  ListSection,
  BulkActionsRow,
  BulkActionButton,
  DeviceList,
  DeviceCard,
  DevicePrimary,
  DeviceMeta,
  CheckWrap,
  Footer,
} from "./Styled/SelectDeviceForProfileComponent.styles";

const PendingProfileApplyToastKey = "_PENDING_PROFILE_APPLY_TOAST_";

function CheckCircleIcon({ active = false }) {
  const fill = active ? "#fdd751" : "#e5e5e5";
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="12" fill={fill} />
      <path
        d="M7 12L10.5 15.5L17 9"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

CheckCircleIcon.propTypes = {
  active: PropTypes.bool,
};

function devicePenLabel(device) {
  if (device.deviceName) {
    return device.deviceName;
  }
  return device.deviceId || "";
}

function SelectDeviceForProfileComponent({ intl, authStore, userStore }) {
  const history = useHistory();
  const location = useLocation();
  const { showErrorToast } = useToast();
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const postProfilePath = useCallback(
    () =>
      localStorage.getItem(store.LocalStorageConnectSkip) ? "/home" : "/pokoroConnect",
    []
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let list = location.state && Array.isArray(location.state.deviceList)
        ? location.state.deviceList
        : null;

      if (!list || list.length < 2) {
        try {
          const res = await userStore.userRepository.getUserDeviceList(
            authStore.loginUser.id
          );
          list = Array.isArray(res.data) ? res.data : [];
        } catch (e) {
          if (!cancelled) {
            setDevices([]);
            setIsReady(true);
          }
          return;
        }
      }

      if (cancelled) {
        return;
      }

      setDevices(list);
      // setSelectedDevices([list[0]]);
      setIsReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authStore.loginUser.id,
    history,
    location.state,
    postProfilePath,
    userStore.userRepository,
  ]);

  const handleSelectDevice = (device) => {
    setSelectedDevices((prev) => {
      const alreadySelected = prev.some((d) => d.deviceId === device.deviceId);
      if (alreadySelected) {
        return prev.filter((d) => d.deviceId !== device.deviceId);
      }
      return [...prev, device];
    });
  };

  const handleCardKeyDown = (event, device) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelectDevice(device);
    }
  };

  const allDevicesSelected =
    devices.length > 0 &&
    devices.every((d) =>
      selectedDevices.some((sd) => sd.deviceId === d.deviceId)
    );

  const handleBulkSelectToggle = () => {
    if (allDevicesSelected) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices([...devices]);
    }
  };

  const handleApply = async () => {
    if (selectedDevices.length === 0 || isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    const selectedDeviceIds = selectedDevices.map((d) => d.deviceId);
    const toastMessage = intl.formatMessage({ id: "selectDeviceForProfile_applyCompleteToast" }, { nickname: authStore.nickname || "" });
    const nextPath = postProfilePath();

    authStore.saveUserDeviceProfiles(selectedDeviceIds, async (saveResponse) => {
      if (!saveResponse) {
        setIsSubmitting(false);
        if (location.state?.returnToPokoroSettingAfterProfile) {
          showErrorToast(
            intl.formatMessage({ id: "profile_registration_error_toast" })
          );
        }
        return;
      }

      if (location.state?.returnToPokoroSettingAfterProfile) {
        setIsSubmitting(false);
        try {
          for (const id of selectedDeviceIds) {
            await userStore.userRepository.updateConnectedDevice(
              authStore.loginUser.id,
              id
            );
          }
          userStore.getUserDeviceList(authStore.loginUser.id, true);
        } catch (e) {
          console.error(e);
          showErrorToast(
            intl.formatMessage({ id: "profile_registration_error_toast" })
          );
          return;
        }
        queuePokoroProfileCompleteReturnToSetting(
          intl.formatMessage({ id: "profile_registration_complete_toast" })
        );
        history.push("/pokoroSetting");
        return;
      }

      const finishApplySuccess = () => {
        localStorage.setItem(PendingProfileApplyToastKey, toastMessage);
        userStore.setSelectedDevice(selectedDevices[0]);
        history.push(nextPath);
      };

      try {
        const res = await userStore.userRepository.getUserDeviceList(
          authStore.loginUser.id
        );
        const list = Array.isArray(res.data) ? res.data : [];
        const hasDeviceWithoutProfile = list.some(
          (device) => device.profile == null
        );

        if (hasDeviceWithoutProfile) {
          authStore.initProfileInfo();
          setIsSubmitting(false);
          localStorage.setItem(PendingProfileApplyToastKey, toastMessage);
          history.push("/signin/nickname", {
            notNewUser: location.state?.notNewUser,
            redirectUrl: location.state?.redirectUrl ?? nextPath,
            returnToPokoroSettingAfterProfile:
              location.state?.returnToPokoroSettingAfterProfile,
          });
          return;
        }
      } catch (e) {
        console.error(e);
      }

      finishApplySuccess();
    });
  };

  if (!isReady) {
    return (
      <PageRoot>
        <Header>
          <Title>
            {intl.formatMessage({ id: "selectDeviceForProfile_title" })}{/* 프로필을 적용할 포코로를 선택해 주세요. */}
          </Title>
        </Header>
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <SettingSubpageHeader
        showBackButton={false}
        title={intl.formatMessage({ id: "selectDeviceForProfile_title" })} /* 프로필을 적용할 포코로를 선택해 주세요. */
        description={`
          ${intl.formatMessage({ id: "selectDeviceForProfile_subtitle_line1" })} 
          ${intl.formatMessage({ id: "selectDeviceForProfile_subtitle_line2" })}

          ${intl.formatMessage({ id: "selectDeviceForProfile_subtitle_line3" })} 
          ${intl.formatMessage({ id: "selectDeviceForProfile_subtitle_line4" })}`}
          /* 프로필은 포코로 펜별로 등록이 필요해요. */
          /* 여러 대를 사용 중이라면 등록이 순차적으로 진행돼요. */
          /* 하나의 프로필을 모든 기기에 한번에 적용할 수 있어요. */
          /* 사용 중인 모든 기기에 프로필이 등록되어야 앱을 이용할 수 있어요. */
      />
      <Main>
        <ListSection>
          <BulkActionsRow>
            <BulkActionButton
              type="button"
              onClick={handleBulkSelectToggle}
            >
              {intl.formatMessage({
                id: allDevicesSelected
                  ? "selectDeviceForProfile_deselectAll" /* 전체 해제 */
                  : "selectDeviceForProfile_selectAll" /* 전체 선택 */,
              })}
            </BulkActionButton>
          </BulkActionsRow>
          <DeviceList
            role="list"
            aria-label={intl.formatMessage({ id: "selectDeviceForProfile_list_label" })} 
          >
            {devices.map((d) => (
              <DeviceCard
                key={d.deviceId}
                $selected={selectedDevices.some((sd) => sd.deviceId === d.deviceId)}
                role="button"
                tabIndex={0}
                onClick={() => handleSelectDevice(d)}
                onKeyDown={(e) => handleCardKeyDown(e, d)}
              >
                <DevicePrimary>{d.deviceName || d.deviceId}</DevicePrimary>
                <DeviceMeta>
                  {d.lastUsedDatetime
                    ? DATE_UTIL.convertUTCToTimeZone(
                        d.lastUsedDatetime,
                        DATE_UTIL.FORMAT.DATE_FORMAT
                      )
                    : "—"}
                </DeviceMeta>
                <CheckWrap>
                  <CheckCircleIcon active={selectedDevices.some((sd) => sd.deviceId === d.deviceId)} />
                </CheckWrap>
              </DeviceCard>
            ))}
          </DeviceList>
        </ListSection>
        <Footer>
          <CommonButton
            text={intl.formatMessage({ id: "selectDeviceForProfile_applyButton" })}
            background="#fdd751"
            color="#333333"
            onClick={handleApply}
            disabled={isSubmitting || selectedDevices.length === 0}
          />
        </Footer>
      </Main>
    </PageRoot>
  );
}

SelectDeviceForProfileComponent.propTypes = {
  intl: PropTypes.object.isRequired,
  authStore: PropTypes.object.isRequired,
  userStore: PropTypes.object.isRequired,
};

export default injectIntl(
  inject("authStore", "userStore")(observer(SelectDeviceForProfileComponent))
);
